import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from '@/hooks/useInView';

const FORM_URL = 'https://readdy.ai/api/form/d96tka7pg5pqhofcusv0';

export default function CtaForm() {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLDivElement>(0.1);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // honeypot
    const honeypot = (formData.get('website_alt') as string) || '';
    if (honeypot.trim() !== '') {
      setStatus('success');
      formEl.reset();
      setMessageLength(0);
      return;
    }
    formData.delete('website_alt');

    // enforce message length
    const message = (formData.get('message') as string) || '';
    if (message.length > 500) {
      setStatus('error');
      setErrorMessage(t('cta.form.error'));
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((v, k) => {
        params.append(k, String(v));
      });
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setErrorMessage(serverMsg || t('cta.form.error'));
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(t('cta.form.error'));
    }
  };

  return (
    <section id="contact" className="w-full bg-foreground-950 py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-25" />
      <div className="absolute top-20 -left-40 w-[500px] h-[500px] bg-primary-500/15 rounded-full blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-accent-500/15 rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: '2s' }} />

      <div ref={ref} className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className={`text-xs font-mono-tech tracking-widest uppercase text-primary-500 mb-6 ${inView ? 'animate-fade-up' : 'opacity-0'}`}>
              {t('cta.tag')}
            </div>
            <h2
              className={`font-heading text-background-50 text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-semibold tracking-tight ${inView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.1s' }}
            >
              {t('cta.title')}
            </h2>
            <p className={`mt-6 text-base text-background-200/80 leading-relaxed ${inView ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              {t('cta.desc')}
            </p>

            <div className={`mt-10 space-y-4 ${inView ? 'animate-fade-up' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
              <a
                href="mailto:hello@angleforty.com"
                className="flex items-center gap-3 text-background-100 hover:text-primary-500 transition-colors group cursor-pointer"
              >
                <span className="w-10 h-10 rounded-full border border-background-50/20 flex items-center justify-center group-hover:border-primary-500 group-hover:bg-primary-500/10">
                  <i className="ri-mail-line text-base w-4 h-4 flex items-center justify-center" />
                </span>
                <span className="font-mono-tech text-sm tracking-wide">hello@angleforty.com</span>
              </a>
              <a
                href="tel:+85231500000"
                className="flex items-center gap-3 text-background-100 hover:text-primary-500 transition-colors group cursor-pointer"
              >
                <span className="w-10 h-10 rounded-full border border-background-50/20 flex items-center justify-center group-hover:border-primary-500 group-hover:bg-primary-500/10">
                  <i className="ri-phone-line text-base w-4 h-4 flex items-center justify-center" />
                </span>
                <span className="font-mono-tech text-sm tracking-wide">+852 3150 0000</span>
              </a>
            </div>
          </div>

          <div
            className={`lg:col-span-3 bg-background-50 rounded-3xl p-6 md:p-10 ${inView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
          >
            <form
              id="af-consult-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Honeypot */}
              <div className="af-extra-field" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-2">
                    {t('cta.form.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-md focus:border-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-2">
                    {t('cta.form.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-md focus:border-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-2">
                  {t('cta.form.company')}
                </label>
                <input
                  type="text"
                  name="company"
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-md focus:border-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-2">
                  {t('cta.form.type')} *
                </label>
                <div className="relative">
                  <select
                    name="projectType"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 pr-10 text-sm bg-background-100 border border-background-200 rounded-md focus:border-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" disabled>—</option>
                    <option value="NFT Art">{t('cta.form.type.nft.art')}</option>
                    <option value="NFT Gaming">{t('cta.form.type.nft.gaming')}</option>
                    <option value="NFT Membership">{t('cta.form.type.nft.membership')}</option>
                    <option value="NFT Marketplace">{t('cta.form.type.nft.marketplace')}</option>
                    <option value="Other">{t('cta.form.type.other')}</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 w-4 h-4 flex items-center justify-center pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-2">
                  {t('cta.form.message')}
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setMessageLength(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-md focus:border-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-all resize-none"
                />
                <div className="mt-1 text-right text-[10px] font-mono-tech text-foreground-500">
                  {messageLength}/500
                </div>
              </div>

              {status === 'success' && (
                <div className="p-4 bg-primary-100 border border-primary-300 text-foreground-900 text-sm rounded-md flex items-center gap-2">
                  <i className="ri-check-line text-base w-4 h-4 flex items-center justify-center" />
                  {t('cta.form.success')}
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 bg-accent-100 border border-accent-300 text-accent-900 text-sm rounded-md flex items-start gap-2">
                  <i className="ri-error-warning-line text-base w-4 h-4 flex items-center justify-center mt-0.5" />
                  <span>{errorMessage || t('cta.form.error')}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="group inline-flex items-center gap-3 pl-1 pr-6 py-1 bg-foreground-950 hover:bg-primary-500 text-background-50 hover:text-foreground-950 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-wait"
              >
                <span className="w-11 h-11 rounded-full bg-primary-500 group-hover:bg-foreground-950 group-hover:text-primary-500 flex items-center justify-center text-foreground-950 transition-colors">
                  <i className="ri-send-plane-line text-lg w-5 h-5 flex items-center justify-center" />
                </span>
                <span>{status === 'submitting' ? t('cta.form.submitting') : t('cta.form.submit')}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}